/**
 * /erp/products — list page (Manager+ via the /erp shell gate).
 *
 * v1 PR-A: simple table sorted by code. No server-side pagination
 * yet — the volume in v1 is well under a thousand rows. Filter by
 * active / all and a free-text q across name + code (client-side
 * since the volume is small).
 */

import { useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Box, Plus, Search } from "lucide-react";
import { useMemo, useState } from "react";

import { ImportDialog } from "../components/ImportDialog";
import { useAuthStore } from "../lib/auth";
import { ERP_QUERY_KEYS, useProducts } from "../lib/erp";

export const Route = createFileRoute("/_app/erp/products/")({
  component: ProductsListPage,
});

function ProductsListPage() {
  const queryClient = useQueryClient();
  const role = useAuthStore((state) => state.user?.role);
  const isProduction = role === "production";
  const [q, setQ] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const productsQ = useProducts();
  const products = productsQ.data?.items ?? [];

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return products.filter((p) => {
      if (!showInactive && !p.is_active) return false;
      if (!needle) return true;
      return (
        p.name.toLowerCase().includes(needle) ||
        p.code.toLowerCase().includes(needle) ||
        p.category?.toLowerCase().includes(needle)
      );
    });
  }, [products, q, showInactive]);

  return (
    <div className="page-shell space-y-4 p-4 sm:p-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <Box className="size-5 opacity-70" aria-hidden />
            Products
          </h1>
          <p className="text-sm opacity-60">
            Sellable / manufacturable items. Codes auto-generate as PRD-NNNNN.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(role === "admin" || role === "manager") && (
            <ImportDialog
            entityLabel="Products"
            importPath="/api/erp/products/import"
            templatePath="/api/erp/products/import/template"
            columns={["code", "name", "category", "uom", "default_sell_price", "notes"]}
            onImported={() => queryClient.invalidateQueries({ queryKey: ERP_QUERY_KEYS.products })}
            />
          )}
          <Link
            to="/erp/products/new"
            className="inline-flex items-center gap-1.5 rounded-md bg-[var(--accent)] px-4 py-3.5 text-sm font-medium text-white hover:opacity-90"
          >
            <Plus className="size-4" aria-hidden /> New Product
          </Link>
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 opacity-50"
            aria-hidden
          />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter by name, code, or category…"
            className="w-full rounded-md border border-[var(--border)] bg-transparent py-1.5 pl-8 pr-3 text-sm outline-none focus:border-[var(--accent)]"
          />
        </div>
        <label className="inline-flex items-center gap-2 text-xs opacity-80">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="size-3.5"
          />
          Show inactive
        </label>
        <span className="text-xs opacity-60">
          {filtered.length} {filtered.length === 1 ? "row" : "rows"}
        </span>
      </div>

      {productsQ.isLoading ? (
        <p className="text-sm opacity-60">Loading…</p>
      ) : productsQ.error ? (
        <p className="text-sm text-red-700 dark:text-red-300">
          Could not load products: {(productsQ.error as Error).message}
        </p>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--border)] p-10 text-center">
          <Box className="mx-auto mb-3 size-8 opacity-40" aria-hidden />
          <p className="text-sm font-medium">
            {products.length === 0 ? "No products yet." : "No products match."}
          </p>
          <p className="mt-1 text-sm opacity-60">
            {products.length === 0
              ? "Create one to get started."
              : "Try a different search or include inactive products."}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[var(--border)]">
          <table className="w-full min-w-[42rem] text-sm">
            <thead className="bg-black/5 text-xs uppercase tracking-wider opacity-70 dark:bg-white/5">
              <tr>
                <th className="px-4 py-3.5 text-left">Code</th>
                <th className="px-4 py-3.5 text-left">Name</th>
                <th className="hidden px-4 py-3.5 text-left sm:table-cell">Category</th>
                <th className="px-4 py-3.5 text-left">UoM</th>
                {!isProduction && <th className="hidden px-4 py-3.5 text-right md:table-cell">Default price</th>}
                {!isProduction && <th className="hidden px-4 py-3.5 text-left lg:table-cell">Linked raw</th>}
                <th className="px-4 py-3.5 text-left">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border)]">
              {filtered.map((p) => (
                <tr
                  key={p.id}
                  className="cursor-pointer transition-colors hover:bg-black/5 dark:hover:bg-white/5"
                >
                  <td className="px-4 py-3.5">
                    <Link
                      to="/erp/products/$productId"
                      params={{ productId: p.id }}
                      className="font-mono text-xs font-medium hover:underline"
                    >
                      {p.code}
                    </Link>
                  </td>
                  <td className="px-4 py-3.5">
                    <Link
                      to="/erp/products/$productId"
                      params={{ productId: p.id }}
                      className="hover:underline"
                    >
                      {p.name}
                    </Link>
                  </td>
                  <td className="hidden px-4 py-3.5 text-xs opacity-70 sm:table-cell">
                    {p.category ?? "—"}
                  </td>
                  <td className="px-4 py-3.5 text-xs opacity-80">{p.uom}</td>
                  {!isProduction && (
                    <td className="hidden px-4 py-3.5 text-right font-mono text-xs md:table-cell">
                      {p.default_sell_price ?? "—"}
                    </td>
                  )}
                  {!isProduction && (
                    <td className="hidden px-4 py-3.5 text-xs opacity-60 lg:table-cell">
                      {p.linked_raw_material_id ? "Yes" : "—"}
                    </td>
                  )}
                  <td className="px-4 py-3.5">
                    <StatusPill active={p.is_active} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatusPill({ active }: { active: boolean }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${
        active
          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
          : "bg-black/10 opacity-60 dark:bg-white/10"
      }`}
    >
      {active ? "Active" : "Inactive"}
    </span>
  );
}
